import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {environment} from '@env';
import {Store} from '@ngxs/store';
import {Observable, throwError} from 'rxjs';
import {ServerResponse, UserProfile} from '@shared/interfaces';
import {AnalyticsService} from '@app/service/util/analytics.service';
import {FirebaseApp} from '@angular/fire/app';
import {
  Auth,
  authState,
  createUserWithEmailAndPassword,
  fetchSignInMethodsForEmail,
  getAuth,
  linkWithCredential,
  sendPasswordResetEmail,
  signInAnonymously,
  signInWithEmailAndPassword,
  AuthProvider,
  signInWithPopup,
  User,
  linkWithPopup, signInWithCredential, unlink
} from '@angular/fire/auth';

import {
  AuthCredential,
  GoogleAuthProvider,
  FacebookAuthProvider,
  EmailAuthProvider,
  OAuthProvider
} from '@firebase/auth';

import {Firestore, doc, docSnapshots, updateDoc} from '@angular/fire/firestore';
import {DocumentData, DocumentSnapshot} from 'rxfire/firestore/interfaces';
import {LoggerService} from '@app/service/logger/logger.service';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  headers: HttpHeaders;
  apiUrl: string;
  userId: string;
  userInfoPath: string;
  auth: Auth;

  constructor(private http: HttpClient, private store: Store, private afApp: FirebaseApp, private firestore: Firestore,
              private analytics: AnalyticsService, private logger: LoggerService) {
    this.apiUrl = environment.apiUrl;
    this.userInfoPath = `users`;
    this.auth = getAuth(this.afApp);
  }

  getUserData(userId): Observable<DocumentSnapshot<DocumentData>> {
    if (!userId) {
      return throwError({message: 'You are not authenticated, please refresh the page!'});
    }

    const userDoc = doc(this.firestore, this.userInfoPath, userId);
    return docSnapshots(userDoc);
  }

  currentUser() {
    return this.auth.currentUser;
  }

  getUrl(url): string {
    return `${this.apiUrl}/${url}`;
  }

  getAuthState() {
    return authState(this.auth);
  }

  signInWithEmail(email: string, password: string) {
    return signInWithEmailAndPassword(this.auth, email, password);
  }

  signInAnon() {
    return signInAnonymously(this.auth);
  }

  async signInWithGoogle() {
    return signInWithPopup(this.auth, this.googleProvider());
  }

  async signInWithFacebook() {
    return signInWithPopup(this.auth, this.facebookProvider());
  }

  signOut() {
    return this.auth.signOut();
  }

  createUser(data) {
    return createUserWithEmailAndPassword(this.auth, data.email, data.password);
  }

  googleProvider() {
    return new GoogleAuthProvider();
  }

  facebookProvider() {
    return new FacebookAuthProvider();
  }

  linkWithCredential(user: User, credential: AuthCredential) {
    return linkWithCredential(user, credential);
  }

  linkWithPopup(user: User, provider: AuthProvider) {
    return linkWithPopup(user, provider);
  }

  linkAnonToEmail(data) {
    const credential = EmailAuthProvider.credential(data.email, data.password);
    return this.linkWithCredential(this.currentUser(), credential);
  }

  async linkAcctToGoogle(linkType: string) {
    // Change the current user to the one I want to link to
    return this.signInWithGoogle();
  }

  async linkAnonAcctToGoogle(linkType: string) {
    const prevUser = this.currentUser();

    // Change the current user to the one I want to link to
    return this.signInWithGoogle()
      .then(result => {
        const dataToLink = {
          userId: result.user.uid,
          name: result.user.displayName,
          email: result.user.email,
          photoUrl: result.user.photoURL,
          oldUserId: prevUser.uid,
          linkType
        };
        // this.logger.info('linkAcctToGoogle dataToLink', JSON.parse(JSON.stringify(dataToLink)));
        return this.linkUserToAcct(dataToLink);
      })
      .then((serverResponse: ServerResponse) => {
        // this.logger.info('linkAcctToGoogle serverResponse', JSON.parse(JSON.stringify(serverResponse)));
        return serverResponse.data.userId;
      });
  }

  linkAcctToFacebook(linkType: string) {
    const prevUser = this.currentUser();
    let currentUser;
    // this.logger.info('previous user ', JSON.parse(JSON.stringify(prevUser)).uid);
    // Change the current user to the one I want to link to
    return this.signInWithFacebook()
      .then(result => {
        // this.logger.info('linkWithFacebook success', JSON.parse(JSON.stringify(result)).user.uid);
        currentUser = result.user;

        // Link facebook by creating credentials and linking the old user to the credentials
        const credential = OAuthProvider.credentialFromResult(result);
        return this.linkWithCredential(prevUser, credential);
      })
      .then(linkResult => {
        // this.logger.info('linkWithFacebook linkResult', JSON.parse(JSON.stringify(linkResult)));
        // After linking the anon or any account to the AuthProvider, resign in with the previous user to make sure it works
        const linkCredential = OAuthProvider.credentialFromResult(linkResult);
        return signInWithCredential(this.auth, linkCredential);
      })
      .then(linkResult => {
        const dataToLink = {
          userId: linkResult.user.uid,
          name: linkResult.user.displayName,
          email: linkResult.user.email,
          photoUrl: linkResult.user.photoURL,
          oldUserId: prevUser.uid,
          linkType
        };
        // this.logger.info('linkWithFacebook dataToLink', JSON.parse(JSON.stringify(dataToLink)));
        return this.linkUserToAcct(dataToLink);
      })
      .then((serverResponse: ServerResponse) => {
        // this.logger.info('linkWithFacebook serverResponse', JSON.parse(JSON.stringify(serverResponse)));
        return serverResponse.data.userId;
      });
  }

  unlinkSocialAccount(providerId: string) {
    return unlink(this.currentUser(), providerId);
  }

  linkAnonAcctToFacebook(linkType: string) {
    const prevUser = this.currentUser();

    // Change the current user to the one I want to link to
    return this.signInWithFacebook()
      .then(result => {
        const dataToLink = {
          userId: result.user.uid,
          name: result.user.displayName,
          email: result.user.email,
          photoUrl: result.user.photoURL,
          oldUserId: prevUser.uid,
          linkType
        };
        // this.logger.info('linkWithFacebook dataToLink', JSON.parse(JSON.stringify(dataToLink)));
        return this.linkUserToAcct(dataToLink);
      })
      .then((serverResponse: ServerResponse) => {
        // this.logger.info('linkWithFacebook serverResponse', JSON.parse(JSON.stringify(serverResponse)));
        return serverResponse.data.userId;
      });
  }

  resetPassword(data) {
    return sendPasswordResetEmail(this.auth, data);
  }

  createUserEntry(data: Partial<UserProfile>) {
    return this.http.post(this.getUrl('profile/create'), data)
      .toPromise();
  }

  createAnonUser(data?: Partial<UserProfile>) {
    return this.http.post(this.getUrl('profile/create-anon-user'), data)
      .toPromise();
  }

  linkUserToAcct(data?: Partial<UserProfile>) {
    return this.http.post(this.getUrl('profile/link-user-to-acct'), data)
      .toPromise();
  }

  fetchSignInMethods(email: string) {
    return fetchSignInMethodsForEmail(this.auth, email);
  }

  changePassword(data) {
    return this.http.post(this.getUrl('profile/change-password'), data)
      .toPromise();
  }

  async editUserData(oldUserData, userData: UserProfile) {
    if (!userData) {
      return Promise.reject({message: 'No data exists!'});
    }
    let emailChanged = false;
    if (oldUserData && oldUserData.email !== userData.email) {
      emailChanged = true;
    }
    return this.http.post(this.getUrl('profile/edit'), {userData, emailChanged})
      .toPromise();
  }

  async editUserPreference(userId: string, key: string, value: boolean) {
    const userDoc = doc(this.firestore, this.userInfoPath, userId);
    return updateDoc(userDoc, {
      [key]: value
    });
  }
}
